import { useState, forwardRef, useImperativeHandle } from 'react';

import {
  Dropzone,
  DropzoneEmptyState,
  MB,
  Progress,
  RENDER_BYTES,
  SuspenseImage,
} from '@/components/common';
import { TrashIcon } from '@/assets/icons';
import { PhotoUploadService } from '@/services';
import { ERROR_FILE_NAME_INVALID, FILE_NAME_VALIDATION_REGEX } from '@/constants';

import {
  DELAY_AFTER_COMPLETION_MS,
  INCREMENT_CHECK_POINT_1,
  INCREMENT_CHECK_POINT_2,
  INCREMENT_VALUE_1,
  INCREMENT_VALUE_2,
  INCREMENT_VALUE_3,
  PROGRESS_MAX,
} from './ProfilePhotoUpload.constants';
import { IProfilePhotoUpload, IProfilePhotoUploadRef } from './ProfilePhotoUpload.interfaces';

const maxSize = Number(process.env.REACT_APP_PHOTO_UPLOAD_MAX_SIZE_IN_BYTES) || 10 * MB;

const ProfilePhotoUpload = forwardRef<IProfilePhotoUploadRef, IProfilePhotoUpload>(
  ({ initialPhotoUrl, initialPhotoFileName, onUpdate }, ref) => {
    const [files, setFiles] = useState<File[] | undefined>();
    const [error, setError] = useState<string>('');
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [uploadComplete, setUploadComplete] = useState<boolean>(!!initialPhotoUrl);
    const [imagePreview, setImagePreview] = useState<string>(initialPhotoUrl || '');

    // Expose methods to parent component via ref
    useImperativeHandle(ref, () => ({
      setError: (errorMessage: string) => {
        setError(errorMessage);
      },
    }));

    const simulateUploadProgress = () => {
      const interval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= PROGRESS_MAX) {
            clearInterval(interval);
            return PROGRESS_MAX;
          }
          // Slower progress as it approaches PROGRESS_MAX
          const increment =
            prev < INCREMENT_CHECK_POINT_1
              ? INCREMENT_VALUE_1
              : prev < INCREMENT_CHECK_POINT_2
              ? INCREMENT_VALUE_2
              : INCREMENT_VALUE_3;
          return Math.min(prev + increment, PROGRESS_MAX);
        });
      }, 200);
    };

    const processUpload = async (file: File) => {
      try {
        setUploadProgress(0);
        setUploadComplete(false);

        // Simulate upload progress for better UX
        simulateUploadProgress();
        const fileId = await PhotoUploadService.uploadPhotoInChunks(file, (progress) => {
          setUploadProgress((prev) => {
            return Math.max(prev, progress);
          });
        });
        await new Promise((resolve) => setTimeout(resolve, DELAY_AFTER_COMPLETION_MS)); // Small delay to ensure progress bar reaches 100%
        onUpdate?.({ photoId: fileId, photoUrl: imagePreview });
        setUploadComplete(true);
      } catch (error) {
        setFiles(undefined);
        setError('Failed to upload image. Please try again.');
      }
    };

    const onDrop = async (files: File[]) => {
      console.log(files);

      // Validate file name
      if (files[0] && !FILE_NAME_VALIDATION_REGEX.test(files[0].name)) {
        setError(ERROR_FILE_NAME_INVALID);
        return;
      }

      setFiles(files);
      setError('');

      // Create preview URL for the image
      if (files[0]) {
        await processUpload(files[0]);
        const previewUrl = URL.createObjectURL(files[0]);
        setImagePreview(previewUrl);
      }
    };

    const onError = (error: Error) => {
      console.error(error);

      // Map react-dropzone error messages to custom messages
      if (error.message.includes('File type must be')) {
        setError('File format not supported. Allowed file type: Image');
      } else if (error.message.includes('File is larger than')) {
        setError(`File is too large. Single file size limit: ${RENDER_BYTES(maxSize)}`);
      } else {
        setError(error.message);
      }
    };

    const onDelete = () => {
      // Revoke the object URL to free memory
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }

      onUpdate?.({ photoId: undefined, photoUrl: undefined });
      setFiles(undefined);
      setImagePreview('');
      setUploadProgress(0);
      setUploadComplete(false);
      setError('');
    };

    return (
      <div className="flex flex-col gap-1.5 border-neutral-n4 w-full">
        <p className="text-sm font-semibold text-neutral-n9">
          Profile Photo
          <span className="ml-1 text-semantic-error-base">*</span>
        </p>
        {uploadComplete && (files?.length || initialPhotoUrl) ? (
          <div className="flex flex-col items-start gap-2">
            <SuspenseImage
              src={imagePreview}
              alt={files?.[0]?.name || initialPhotoFileName || 'Profile photo'}
              className="h-40 w-[120px] object-cover"
            />
            <div className="flex flex-wrap items-center gap-4">
              <p className="text-sm font-medium text-wrap text-brand-primary-darkest truncate">
                {files?.[0]?.name || initialPhotoFileName || 'Uploaded photo'}
              </p>
              <button
                onClick={onDelete}
                className="flex items-center justify-between gap-2.5 text-semantic-error-base font-semibold text-base"
                aria-label="Delete image"
              >
                <TrashIcon className="w-5 h-5" />
                Delete
              </button>
            </div>
          </div>
        ) : files && files.length > 0 ? (
          <Progress
            value={uploadProgress}
            title={files ? files[0].name : ''}
            className="max-w-[456px]"
          />
        ) : (
          <Dropzone
            accept={JSON.parse(
              process.env.REACT_APP_PHOTO_UPLOAD_ACCEPTED_FORMATS ||
                '{"image/jpeg":[".jpg",".jpeg"],"image/png":[".png"]}',
            )}
            maxSize={maxSize}
            onDrop={onDrop}
            onError={onError}
            src={files}
            error={error}
          >
            <DropzoneEmptyState />
          </Dropzone>
        )}
      </div>
    );
  },
);

ProfilePhotoUpload.displayName = 'ProfilePhotoUpload';

export default ProfilePhotoUpload;
